import Phaser from 'phaser';
import { SpriteRenderer } from '../rendering/SpriteRenderer';
import { ITEM_DEFINITIONS, CONSUMABLE_DEFINITIONS } from '../data/items';
import { RESOURCE_INFO, ResourceType, ALL_RESOURCES, ResourceStock } from '../systems/ResourceManager';
import { FONT_BODY } from './Typography';

// === HELPER POUR AFFICHER DES ICÔNES SVG ===
// Remplace les emojis par des sprites d'icônes 16×16 dans l'UI.

/** Affiche une icône SVG à la position donnée (retourne le sprite ou null si texture manquante) */
export function addIcon(scene: Phaser.Scene, x: number, y: number, iconId: string): Phaser.GameObjects.Sprite | null {
  const key = SpriteRenderer.iconKey(iconId);
  if (!scene.textures.exists(key)) return null;
  return scene.add.sprite(x, y, key).setOrigin(0, 0.5);
}

/** Récupère l'iconId d'un item (équipement) par son definitionId */
export function getItemIconId(definitionId: string): string | null {
  const def = ITEM_DEFINITIONS[definitionId];
  return def?.iconId ?? null;
}

/** Récupère l'iconId d'un consommable par son definitionId */
export function getConsumableIconId(definitionId: string): string | null {
  const def = CONSUMABLE_DEFINITIONS[definitionId];
  return def?.iconId ?? null;
}

/** Récupère l'iconId d'une ressource */
export function getResourceIconId(type: ResourceType): string {
  const map: Record<ResourceType, string> = {
    wood: 'resource-bois',
    iron: 'resource-fer',
    cloth: 'resource-tissu',
    crystal: 'resource-cristal',
    cursed_bone: 'resource-os-maudit',
  };
  return map[type];
}

/** Récupère l'emoji d'un item (fallback si pas d'icône SVG) */
export function getItemEmoji(definitionId: string): string {
  const def = ITEM_DEFINITIONS[definitionId] ?? CONSUMABLE_DEFINITIONS[definitionId];
  return def?.emoji ?? '?';
}

/**
 * Affiche une icône d'item (équipement ou consommable) avec fallback emoji.
 * Retourne le x final (après l'icône) pour positionner le texte à côté.
 */
export function addItemIcon(
  scene: Phaser.Scene,
  x: number,
  y: number,
  definitionId: string,
): { sprite: Phaser.GameObjects.Sprite | null; nextX: number } {
  const iconId = getItemIconId(definitionId) ?? getConsumableIconId(definitionId);
  if (iconId) {
    const sprite = addIcon(scene, x, y, iconId);
    if (sprite) return { sprite, nextX: x + 20 };
  }
  return { sprite: null, nextX: x };
}

/**
 * Affiche une ligne de coût/gain en ressources avec icônes SVG + texte.
 * Retourne tous les GameObjects créés (pour les ajouter à un panel scrollable).
 * @param showStock Si true, affiche "needed/have" (mode coût). Sinon juste la quantité.
 * @param opts.maxWidth Largeur max disponible — active le retour à la ligne automatique si dépassé.
 * @param opts.lineHeight Hauteur d'une ligne en cas de retour (défaut 13).
 */
export function addResourceCostLine(
  scene: Phaser.Scene,
  x: number,
  y: number,
  cost: Partial<ResourceStock>,
  stock?: ResourceStock,
  opts?: { fontSize?: string; depth?: number; maxWidth?: number; lineHeight?: number },
): Phaser.GameObjects.GameObject[] {
  const objects: Phaser.GameObjects.GameObject[] = [];
  const fontSize = opts?.fontSize ?? '10px';
  const depth = opts?.depth ?? 0;
  const maxWidth = opts?.maxWidth;
  const lineHeight = opts?.lineHeight ?? 13;
  let cx = x;
  let cy = y;

  for (const type of ALL_RESOURCES) {
    const amount = cost[type as ResourceType];
    if (!amount || amount <= 0) continue;

    const info = RESOURCE_INFO[type as ResourceType];
    const iconId = getResourceIconId(type as ResourceType);
    const iconKey = SpriteRenderer.iconKey(iconId);

    // Retour à la ligne si l'entrée suivante dépasserait maxWidth
    if (maxWidth !== undefined && cx > x) {
      // Estimer la largeur de cette entrée (icône + texte approx. 5.5px/char + espacement)
      // LOT C2 : format possédé/requis (have/need) — convention standard jeu
      const label = stock
        ? `${info.name} ${stock[type as ResourceType]}/${amount}`
        : `${info.name} ×${amount}`;
      const estimatedWidth = 18 + label.length * 5.5 + 10;
      if ((cx - x) + estimatedWidth > maxWidth) {
        cx = x;
        cy += lineHeight;
      }
    }

    // Icône SVG ou fallback emoji
    if (scene.textures.exists(iconKey)) {
      const sprite = scene.add.sprite(cx + 8, cy, iconKey).setDepth(depth);
      objects.push(sprite);
      cx += 18;
    } else {
      const emojiText = scene.add.text(cx, cy, info.emoji, {
        fontSize, fontFamily: FONT_BODY,
      }).setOrigin(0, 0.5).setDepth(depth);
      objects.push(emojiText);
      cx += 16;
    }

    // Texte : "Nom have/need" (possédé/requis) ou "Nom ×amount"
    // LOT C2 : format inversé pour matcher la convention standard des jeux
    // (possédé en premier = ce que le joueur LIT en premier).
    let label: string;
    let color = '#bdc3c7';
    if (stock) {
      const have = stock[type as ResourceType];
      const ok = have >= amount;
      label = `${info.name} ${have}/${amount}`;
      color = ok ? '#2ecc71' : '#e74c3c';  // vert si suffisant, rouge sinon
    } else {
      label = `${info.name} ×${amount}`;
    }

    const text = scene.add.text(cx, cy, label, {
      fontSize, color, fontFamily: FONT_BODY,
    }).setOrigin(0, 0.5).setDepth(depth);
    objects.push(text);
    cx += text.width + 10;
  }

  return objects;
}
